import { Button, ButtonProps } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import noop from 'lodash/noop';
import {
  ComponentType,
  createRef,
  CSSProperties,
  Fragment,
  PureComponent,
  ReactNode,
} from 'react';
import type { WrappedFieldArrayProps } from 'redux-form';

import { FlowType } from 'fable/flowEditor';

import styles from './fieldRepeater.module.scss';
import { RepeaterDivider } from './repeaterDivider';

export type FieldRepeaterRowProps<TRowProps> = TRowProps & RowProps<TRowProps>;
interface RowProps<TRowProps = any> {
  flowType: FlowType;
  field: string;
  index: number;
  onRemove: (index: number) => void;
  totalRows: number;
  meta: FieldRepeaterProps<TRowProps>['meta'];
}
interface FieldRepeaterButtonProps extends ButtonProps {
  placement?: 'top' | 'bottom' | 'outside';
}

export interface FieldRepeaterProps<TRowProps = any>
  extends WrappedFieldArrayProps {
  actions?: ComponentType<WrappedFieldArrayProps>;
  buttonProps?: FieldRepeaterButtonProps;
  className?: string;
  defaultRowProps: TRowProps | (() => TRowProps);
  divider?: ReactNode;
  onAddRow: () => void;
  onMount: () => void;
  rowComponent: ComponentType<FieldRepeaterRowProps<TRowProps>>;
  rowProps: TRowProps;
  style?: CSSProperties;
  title?: string | ReactNode;
  withBackground?: boolean;
}

const DEFAULT_BUTTON_PROPS: FieldRepeaterButtonProps = {
  label: 'Add',
  placement: 'top',
};

export class FieldRepeater extends PureComponent<FieldRepeaterProps> {
  static defaultProps = {
    defaultRowProps: {},
    rowProps: {},
    onAddRow: noop,
    onMount: noop,
    withBackground: true,
  };

  get buttonProps(): FieldRepeaterButtonProps {
    const { buttonProps } = this.props;

    return buttonProps
      ? { ...DEFAULT_BUTTON_PROPS, ...buttonProps }
      : DEFAULT_BUTTON_PROPS;
  }

  rowsRef: {
    current: null | HTMLDivElement;
  };

  constructor(props: FieldRepeaterProps) {
    super(props);

    this.rowsRef = createRef();
    this.handleAddClick = this.handleAddClick.bind(this);
  }

  componentDidMount() {
    this.props.onMount();
  }

  componentDidUpdate(prevProps: FieldRepeaterProps) {
    const { fields } = this.props;
    if (fields.length > prevProps.fields.length && this.rowsRef.current) {
      const childCount = this.rowsRef.current.childNodes.length;
      const lastElement = this.rowsRef.current.childNodes[childCount - 1];
      if (lastElement instanceof HTMLElement) {
        lastElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  handleAddClick(): void {
    const { defaultRowProps, fields, onAddRow } = this.props;
    onAddRow();
    fields.push(
      typeof defaultRowProps === 'function'
        ? defaultRowProps()
        : defaultRowProps,
    );
  }

  render() {
    const {
      buttonProps,
      className,
      defaultRowProps,
      rowComponent: RowComponent,
      title,
      fields,
      meta,
      actions: Actions,
      divider,
      rowProps,
      style,
      withBackground,
      onAddRow,
      onMount,
      ...otherProps
    } = this.props;

    const rootClass = classnames(
      {
        [styles.withBackground]: withBackground,
      },
      className,
    );

    const {
      className: buttonClassName,
      label: buttonLabel,
      placement: buttonPlacement,
      icon: buttonIcon,
    } = this.buttonProps;

    const shouldRenderHeader = buttonPlacement === 'top' || !!title;

    return (
      <>
        <div {...otherProps} className={rootClass}>
          {shouldRenderHeader && (
            <div className={styles.header}>
              {typeof title === 'string' ? (
                <h2 className={styles.title}>{title}</h2>
              ) : (
                title
              )}
              {/* if addButtonProps were provided, render add button at the bottom */}
              {buttonPlacement === 'top' && (
                <div className={styles.actions}>
                  {!!Actions && <Actions fields={fields} meta={meta} />}
                  <button
                    type="button"
                    className={classnames(styles.action, buttonClassName)}
                    onClick={this.handleAddClick}
                  >
                    {buttonLabel}
                  </button>
                </div>
              )}
            </div>
          )}

          {!meta.pristine && meta.error && (
            <div className={styles.error}>{meta.error}</div>
          )}

          {fields.length > 0 && (
            <div className={styles.rows} style={style || {}} ref={this.rowsRef}>
              {fields.map((field, index) => (
                <Fragment key={field}>
                  <RowComponent
                    {...rowProps}
                    field={field}
                    index={index}
                    meta={meta}
                    onRemove={fields.remove}
                    totalRows={fields.length}
                  />
                  {index < fields.length - 1 && !!divider && divider}
                </Fragment>
              ))}
              {buttonPlacement === 'bottom' && (
                <>
                  <RepeaterDivider />
                  <Button
                    className={classnames(styles.addButton, buttonClassName)}
                    onClick={this.handleAddClick}
                    variant="secondary"
                    icon={buttonIcon}
                  >
                    {buttonLabel}
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
        {buttonPlacement === 'outside' && (
          <Button
            className={classnames(styles.addButton, buttonClassName)}
            onClick={this.handleAddClick}
            variant="secondary"
            icon={buttonIcon}
          >
            {buttonLabel}
          </Button>
        )}
      </>
    );
  }
}
